import { FC, useState, useEffect, useMemo, useRef } from 'react';
import { Grid, Button, Box, Typography, CircularProgress, Card, CardContent, useTheme } from '@mui/material';
import { ArrowBack, CheckCircle, Cancel, Schedule, EventBusy, TrendingUp } from '@mui/icons-material';
import PaginatedGrid from '../paginatedGrid/PaginatedGrid';
import { GridColDef } from '@mui/x-data-grid';
import RowActions from '../RowActions';
import { isEmpty } from 'lodash-es';
import { IUserBasedAttendanceComponentProps } from './attendanceComponent.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  TAttendanceByUserRecord,
  TAttendanceByUserResponse,
  ATTENDANCE_STATUS,
} from '../../../apis/__shared/attendance.interfaces';
import { useResponsive } from 'src/hooks/useResponsiveHook';

const UserBasedAttendanceComponent: FC<IUserBasedAttendanceComponentProps> = ({
  atendanceRecords,
  onEditAttendanceClickHandler,
  onFetchUserAttendance,
  userId,
  onBackToDateView,
  hideBackButton = false,
}) => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const [attendanceData, setAttendanceData] = useState<
    TAttendanceByUserResponse | undefined
  >(atendanceRecords);
  const [isLoading, setIsLoading] = useState(false);
  const fetchFnRef = useRef(onFetchUserAttendance);
  fetchFnRef.current = onFetchUserAttendance;
  const manualFetchInFlightRef = useRef(false);
  /** Avoid retry storms when API returns `{}` or fetch completes without usable rows. */
  const fetchAttemptedForUserIdRef = useRef<string | undefined>();
  const prevUserIdRef = useRef<string | undefined>();

  useEffect(() => {
    if (prevUserIdRef.current === userId) return;
    fetchAttemptedForUserIdRef.current = undefined;
    if (prevUserIdRef.current !== undefined) {
      setAttendanceData(undefined);
    }
    prevUserIdRef.current = userId;
  }, [userId]);

  // Sync from React Query when it has data. Do not treat `{}` as valid parent data — it is
  // truthy and would overwrite a successful client fetch, causing a refetch loop on by-user.
  useEffect(() => {
    if (atendanceRecords && !isEmpty(atendanceRecords)) {
      setAttendanceData(atendanceRecords);
      manualFetchInFlightRef.current = false;
      return;
    }
    const fetchFn = fetchFnRef.current;
    if (
      !userId ||
      !fetchFn ||
      fetchAttemptedForUserIdRef.current === userId ||
      !isEmpty(attendanceData) ||
      manualFetchInFlightRef.current
    ) {
      return;
    }
    manualFetchInFlightRef.current = true;
    setIsLoading(true);
    fetchFn(userId)
      .then((data) => {
        setAttendanceData(data);
      })
      .catch((error) => {
        console.error('Error fetching user attendance:', error);
      })
      .finally(() => {
        fetchAttemptedForUserIdRef.current = userId;
        manualFetchInFlightRef.current = false;
        setIsLoading(false);
      });
  }, [userId, atendanceRecords, attendanceData]);

  const handleBackToGrid = () => {
    if (onBackToDateView) {
      onBackToDateView();
    }
  };

  const handleEditClick = (record: TAttendanceByUserRecord) => {
    // Convert TAttendanceByUserRecord to include userDetails for the tracker
    if (currentUserData) {
      const recordWithUserDetails = {
        ...record,
        userDetails: currentUserData.userDetails,
      };
      onEditAttendanceClickHandler(recordWithUserDetails);
    } else {
      onEditAttendanceClickHandler(record);
    }
  };

  const onRowClickHandler = (data: any) => {
    // The row data contains the record directly
    if (data.record) {
      handleEditClick(data.record);
    }
  };

  // Get user data for the current userId
  const currentUserData = useMemo(() => {
    if (isEmpty(attendanceData) || !userId) return null;
    return attendanceData[userId] || null;
  }, [attendanceData, userId]);

  // Get username
  const username = useMemo(() => {
    if (!currentUserData) return '';
    const { firstName, middleName, lastName } = currentUserData.userDetails;
    return [firstName, middleName, lastName].filter(Boolean).join(' ') || userId || '';
  }, [currentUserData, userId]);

  // Get summary metrics
  const summary = useMemo(() => {
    return currentUserData?.summary || null;
  }, [currentUserData]);

  const getAttendanceGridRowsToRender = () => {
    if (!currentUserData) return [];

    return currentUserData.attendanceRecords.map((record: TAttendanceByUserRecord) => ({
      id: record.attendanceId || record.date,
      date: record.date,
      status: record.attendanceStatus,
      comment: record.comment || '',
      record: record,
    }));
  };

  const getColumnsForGrid = () => {
    const columns: GridColDef[] = [
      {
        field: 'date',
        headerName: translateText('attendance.date'),
        width: 180,
        flex: 1,
      },
      {
        field: 'status',
        headerName: translateText('attendance.details.status'),
        width: 150,
        renderCell: (params) => {
          const status = params.value as ATTENDANCE_STATUS;
          return translateText(`attendance.status.${status}`) || status;
        },
      },
      {
        field: 'comment',
        headerName: translateText('attendance.comment'),
        width: 200,
        flex: 1,
        renderCell: (params) => {
          return params.value || '-';
        },
      },
      {
        field: 'actions',
        headerName: translateText('attendance.actions'),
        width: 120,
        renderCell: (params) => {
          return (
            <RowActions record={params} onRowClickHandler={onRowClickHandler} />
          );
        },
      },
    ];
    return columns;
  };

  if (isLoading) {
    return (
      <Grid
        container
        spacing={0}
        sx={{
          height: '100%',
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 2 },
        }}
        data-testid="user-based-attendance-component"
      >
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: { xs: '300px', sm: '400px' },
          }}
          data-testid="loading-user-attendance-data"
        >
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  if (isEmpty(attendanceData) || !currentUserData) {
    return (
      <Grid
        container
        spacing={2}
        sx={{
          height: '100%',
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 2 },
        }}
        data-testid="user-based-attendance-component"
      >
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 },
          }}
        >
          {!hideBackButton && (
            <Box
              display="flex"
              alignItems="center"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={{ xs: 1, sm: 0 }}
              width={{ xs: '100%', sm: 'auto' }}
              data-testid="attendance-detail-header"
            >
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToGrid}
                sx={{
                  mr: { xs: 0, sm: 2 },
                  minHeight: { xs: '44px', sm: '36px' },
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  width: { xs: '100%', sm: 'auto' },
                }}
                data-testid="back-to-grid-button"
              >
                {translateText('attendance.backToGrid')}
              </Button>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                  fontWeight: { xs: 500, sm: 600 },
                  textAlign: { xs: 'center', sm: 'left' },
                  width: { xs: '100%', sm: 'auto' },
                }}
                data-testid="attendance-detail-header-title"
              >
                {`${username || userId || ''} ${translateText('attendance.details.title')}`}
              </Typography>
            </Box>
          )}
          {hideBackButton && (
            <Box
              data-testid="attendance-primary-header"
              width={{ xs: '100%', sm: 'auto' }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                  fontWeight: { xs: 500, sm: 600 },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
                data-testid="attendance-grid-title"
              >
                {`${username || userId || ''} ${translateText('attendance.details.title')}`}
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
            data-testid="no-attendance-data"
          >
            {translateText('attendance.noData')}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing={2}
      sx={{
        height: '100%',
        px: { xs: 1, sm: 2 },
        py: { xs: 1, sm: 2 },
      }}
      data-testid="user-based-attendance-component"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 0 },
        }}
      >
        {!hideBackButton && (
          <Box
            display="flex"
            alignItems="center"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 1, sm: 0 }}
            width={{ xs: '100%', sm: 'auto' }}
            data-testid="attendance-detail-header"
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToGrid}
              sx={{
                mr: { xs: 0, sm: 2 },
                minHeight: { xs: '44px', sm: '36px' },
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                width: { xs: '100%', sm: 'auto' },
              }}
              data-testid="back-to-grid-button"
            >
              {translateText('attendance.backToGrid')}
            </Button>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: { xs: 500, sm: 600 },
                textAlign: { xs: 'center', sm: 'left' },
                width: { xs: '100%', sm: 'auto' },
              }}
              data-testid="attendance-detail-header-title"
            >
              {`${username} ${translateText('attendance.details.title')}`}
            </Typography>
          </Box>
        )}
        {hideBackButton && (
          <Box
            data-testid="attendance-primary-header"
            width={{ xs: '100%', sm: 'auto' }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: { xs: 500, sm: 600 },
                textAlign: { xs: 'center', sm: 'left' },
              }}
              data-testid="attendance-grid-title"
            >
              {`${username} ${translateText('attendance.details.title')}`}
            </Typography>
          </Box>
        )}
      </Grid>

      {/* Summary Cards */}
      {summary && (
        <Grid item xs={12}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={6} sm={6} md={2.4}>
              <Card
                data-testid="attendance-summary-present"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.success.light}15 0%, ${theme.palette.success.light}05 100%)`,
                  border: `1px solid ${theme.palette.success.light}40`,
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${theme.palette.success.light}30`,
                  },
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <CheckCircle
                      sx={{
                        color: theme.palette.success.main,
                        fontSize: { xs: 24, sm: 28, md: 32 },
                      }}
                    />
                  </Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                    fontWeight={500}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('attendance.summary.totalPresent')}
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    color={theme.palette.success.main}
                    sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}
                  >
                    {summary.totalPresent || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <Card
                data-testid="attendance-summary-absent"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.error.light}15 0%, ${theme.palette.error.light}05 100%)`,
                  border: `1px solid ${theme.palette.error.light}40`,
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${theme.palette.error.light}30`,
                  },
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Cancel
                      sx={{
                        color: theme.palette.error.main,
                        fontSize: { xs: 24, sm: 28, md: 32 },
                      }}
                    />
                  </Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                    fontWeight={500}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('attendance.summary.totalAbsent')}
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    color={theme.palette.error.main}
                    sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}
                  >
                    {summary.totalAbsent || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <Card
                data-testid="attendance-summary-late"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.warning.light}15 0%, ${theme.palette.warning.light}05 100%)`,
                  border: `1px solid ${theme.palette.warning.light}40`,
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${theme.palette.warning.light}30`,
                  },
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Schedule
                      sx={{
                        color: theme.palette.warning.main,
                        fontSize: { xs: 24, sm: 28, md: 32 },
                      }}
                    />
                  </Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                    fontWeight={500}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('attendance.summary.totalLate')}
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    color={theme.palette.warning.main}
                    sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}
                  >
                    {summary.totalLate || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <Card
                data-testid="attendance-summary-halfday"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.info.light}15 0%, ${theme.palette.info.light}05 100%)`,
                  border: `1px solid ${theme.palette.info.light}40`,
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${theme.palette.info.light}30`,
                  },
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <EventBusy
                      sx={{
                        color: theme.palette.info.main,
                        fontSize: { xs: 24, sm: 28, md: 32 },
                      }}
                    />
                  </Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                    fontWeight={500}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('attendance.summary.totalHalfDay')}
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    color={theme.palette.info.main}
                    sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}
                  >
                    {summary.totalHalfDay || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card
                data-testid="attendance-summary-rate"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.light}05 100%)`,
                  border: `1px solid ${theme.palette.primary.light}40`,
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${theme.palette.primary.light}30`,
                  },
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <TrendingUp
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: { xs: 24, sm: 28, md: 32 },
                      }}
                    />
                  </Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                    fontWeight={500}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('attendance.summary.attendanceRate')}
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    color={theme.palette.primary.main}
                    sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}
                  >
                    {summary.attendanceRate ? `${summary.attendanceRate.toFixed(1)}%` : '0%'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* Attendance Grid */}
      <Grid
        item
        xs={12}
        data-testid="attendance-grid-container"
        sx={{
          maxHeight: {
            xs: 'calc(100vh - 500px)',
            sm: 'calc(100vh - 450px)',
            md: 'calc(100vh - 400px)'
          },
          height: 'fit-content',
          overflow: 'auto',
        }}
      >
        <PaginatedGrid
          rows={getAttendanceGridRowsToRender()}
          columns={getColumnsForGrid()}
          dataTestId="user-based-attendance-grid"
          onRowClick={onRowClickHandler}
          hideColumnsOnMobile={isMobile ? ['comment'] : []}
          mobilePageSize={5}
        />
      </Grid>
    </Grid>
  );
};

export default UserBasedAttendanceComponent;
